import React, { Fragment } from 'react';

import { cssClasses } from 'pkg/css/utils';
import useTooltip from 'pkg/hooks/useTooltip';

import type { CustomSymbolVariant } from 'components/custom-symbols/symbols';
import { CustomSymbols } from 'components/custom-symbols/symbols';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';
import { MaterialSymbols } from 'components/material-symbols/symbols';

import * as css from './styles.css';

// Any symbol name accepted by MaterialSymbol — Material or custom. Use this
// when broadening icon props that today only accept MaterialSymbolVariant.
export type SymbolVariant = MaterialSymbolVariant | CustomSymbolVariant;

export enum IconSizeMap {
	default = '20px',
	small = '18px',
	large = '24px',
	xl = '28px',
	xxl = '48px',
}

interface MaterialSymbolProps {
	variant: SymbolVariant;
	scale?: number;
	className?: string;
	fill?: string;
	rotate?: number;
	style?: React.CSSProperties;
	actualSize?: boolean;
	tooltip?: string;
	// CSS size value (e.g., "24px", "1.5rem") that sets both width and height to maintain 1:1 aspect ratio
	size?: string;
	testid?: string;
}

export default function MaterialSymbol({
	variant,
	scale = 1,
	rotate,
	actualSize = false,
	className = '',
	fill,
	style,
	tooltip,
	size,
	testid,
}: MaterialSymbolProps) {
	// symbol might yield undefined, it's handled after `useTooltip` to respect react hook rules
	const symbol = variant?.startsWith('custom_')
		? CustomSymbols[variant as CustomSymbolVariant]
		: MaterialSymbols[variant as MaterialSymbolVariant];

	const { onMouseEnter, tooltip: tip } = useTooltip(tooltip);

	if (!symbol) {
		return null;
	}

	const combinedStyle = {
		'--scale': scale.toString(),
		color: fill,
		transform: rotate ? `rotate(${rotate}deg)` : undefined,
		'--size': size,
		...style,
	};

	return (
		<Fragment>
			{tip}
			<span
				onMouseEnter={onMouseEnter}
				role="img"
				data-icon={variant}
				data-testid={testid}
				className={cssClasses(
					css.materialSymbol,
					actualSize ? css.actualSize : '',
					size ? css.customSize : '',
					className
				)}
				style={combinedStyle as React.CSSProperties}>
				{symbol}
			</span>
		</Fragment>
	);
}
